// Must be evaluated before anything it instruments (fetch, XHR, console, error handlers).
import './telemetry.ts';
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import { ThemeProvider } from './components/theme-provider.tsx';
import { PinnedDirectoriesProvider } from './contexts/PinnedDirectoriesContext.tsx';
import RouteErrorBoundary from './components/RouteErrorBoundary.tsx';
import { reportContentSecurityPolicyViolations } from './lib/csp-violations.ts';
import { clearStaleChunkReloadFlag, reloadOnceForStaleChunk } from './lib/stale-chunk-reload.ts';

reportContentSecurityPolicyViolations();

// After a deploy, a stale tab's lazy chunks are gone. Vite reports the failed import here;
// the error still reaches RouteErrorBoundary, which shows it if the guarded reload is spent.
window.addEventListener('vite:preloadError', () => {
  reloadOnceForStaleChunk();
});

// Dash0 observes History API changes and records SPA page views automatically.
const router = createBrowserRouter([
  {
    ErrorBoundary: RouteErrorBoundary,
    children: [
      {
        path: "/",
        lazy: async () => {
          const data = await import('./pages/Root.data.ts');
          return {
            loader: data.rootLoader,
          };
        },
      },
      {
        path: "/files",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Files.tsx'), import('./pages/Files.data.ts')]);
          return {
            Component: module.default,
            loader: data.filesLoader,
          };
        },
      },
      {
        path: "/files/:fileId",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/FileViewer.tsx'), import('./pages/FileViewer.data.ts')]);
          return {
            Component: module.default,
            loader: data.fileViewerLoader,
          };
        },
      },
      {
        path: "/search",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Search.tsx'), import('./pages/Search.data.ts')]);
          return {
            Component: module.default,
            loader: data.searchLoader,
          };
        },
      },
      {
        path: "/uploads",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Uploads.tsx'), import('./pages/Uploads.data.ts')]);
          return {
            Component: module.default,
            loader: data.uploadsLoader,
          };
        },
      },
      {
        path: "/directories",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Directories.tsx'), import('./pages/Directories.data.ts')]);
          return {
            Component: module.default,
            loader: data.directoriesLoader,
          };
        },
      },
      {
        path: "/directories/:id",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Directories.tsx'), import('./pages/Directories.data.ts')]);
          return {
            Component: module.default,
            loader: data.directoriesLoader,
          };
        },
      },
      {
        path: "/jobs",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Jobs.tsx'), import('./pages/Jobs.data.ts')]);
          return {
            Component: module.default,
            loader: data.jobsLoader,
            action: data.jobsAction,
          };
        },
      },
      {
        path: "/trash",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Trash.tsx'), import('./pages/Trash.data.ts')]);
          return {
            Component: module.default,
            loader: data.trashLoader,
          };
        },
      },
      {
        path: "/settings",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Settings.tsx'), import('./pages/Settings.data.ts')]);
          return {
            Component: module.default,
            loader: data.settingsLoader,
            action: data.settingsAction,
          };
        },
      },
      {
        path: "/settings/billing",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Billing.tsx'), import('./pages/Billing.data.ts')]);
          return {
            Component: module.default,
            loader: data.billingLoader,
            action: data.billingAction,
          };
        },
      },
      {
        path: "/settings/watermarks",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/ImageWatermarks.tsx'), import('./pages/ImageWatermarks.data.ts')]);
          return {
            Component: module.default,
            loader: data.imageWatermarksLoader,
            action: data.imageWatermarksAction,
          };
        },
      },
      {
        path: "/settings/watermarks/identify",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/IdentifyWatermark.tsx'), import('./pages/IdentifyWatermark.data.ts')]);
          return {
            Component: module.default,
            loader: data.identifyWatermarkLoader,
          };
        },
      },
      {
        path: "/plans",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Plans.tsx'), import('./pages/Plans.data.ts')]);
          return {
            Component: module.default,
            loader: data.plansLoader,
            action: data.plansAction,
          };
        },
      },
      {
        path: "/plans/:productId",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Plans.tsx'), import('./pages/Plans.data.ts')]);
          return {
            Component: module.default,
            loader: data.plansLoader,
            action: data.plansAction,
          };
        },
      },
      {
        path: "/billing/return",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/BillingReturn.tsx'), import('./pages/BillingReturn.data.ts')]);
          return {
            Component: module.default,
            loader: data.billingReturnLoader,
          };
        },
      },
      {
        path: "/login",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Login.tsx'), import('./pages/Login.data.ts')]);
          return {
            Component: module.default,
            action: data.loginAction,
            loader: data.loginLoader,
          };
        },
      },
      {
        path: "/accept-invitation",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/AcceptInvitation.tsx'), import('./pages/AcceptInvitation.data.ts')]);
          return {
            Component: module.default,
            loader: data.acceptInvitationLoader,
            action: data.acceptInvitationAction,
          };
        },
      },
      {
        path: "/create-organisation",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/CreateOrganisation.tsx'), import('./pages/CreateOrganisation.data.ts')]);
          return {
            Component: module.default,
            action: data.createOrganisationAction,
            loader: data.createOrganisationLoader,
          };
        },
      },
      {
        path: "/select-organisation",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/SelectOrganisation.tsx'), import('./pages/SelectOrganisation.data.ts')]);
          return {
            Component: module.default,
            action: data.selectOrganisationAction,
            loader: data.selectOrganisationLoader,
          };
        },
      },
      {
        path: "/signup",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Signup.tsx'), import('./pages/Signup.data.ts')]);
          return {
            Component: module.default,
            action: data.signupAction,
            loader: data.signupLoader,
          };
        },
      },
      {
        path: "/signup/:productId",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/Signup.tsx'), import('./pages/Signup.data.ts')]);
          return {
            Component: module.default,
            action: data.signupAction,
            loader: data.signupLoader,
          };
        },
      },
      {
        path: "/forgot-password",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/ForgotPassword.tsx'), import('./pages/ForgotPassword.data.ts')]);
          return {
            Component: module.default,
            action: data.forgotPasswordAction,
          };
        },
      },
      {
        path: "/reset-password",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/ResetPassword.tsx'), import('./pages/ResetPassword.data.ts')]);
          return {
            Component: module.default,
            action: data.resetPasswordAction,
          };
        },
      },
      {
        path: "/shared/files/:token",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/SharedContent.tsx'), import('./pages/SharedContent.data.ts')]);
          return {
            Component: module.default,
            loader: data.sharedFileLoader,
          };
        },
      },
      {
        path: "/shared/revisions/:token",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/SharedContent.tsx'), import('./pages/SharedContent.data.ts')]);
          return {
            Component: module.default,
            loader: data.sharedRevisionLoader,
          };
        },
      },
      {
        path: "/shared/directories/:token",
        lazy: async () => {
          const [module, data] = await Promise.all([import('./pages/SharedDirectory.tsx'), import('./pages/SharedDirectory.data.ts')]);
          return {
            Component: module.default,
            loader: data.sharedDirectoryLoader,
          };
        },
      },
      {
        path: "*",
        lazy: async () => {
          const module = await import('./pages/NotFound.tsx');
          return { Component: module.default };
        },
      },
    ],
  },
]);

router.subscribe((state) => {
  if (state.initialized && state.navigation.state === 'idle' && !state.errors) {
    clearStaleChunkReloadFlag();
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <PinnedDirectoriesProvider>
        <RouterProvider router={router} />
      </PinnedDirectoriesProvider>
    </ThemeProvider>
  </StrictMode>,
)
